import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    marketingBlogEntity,
    marketingBlogStatusEntity,
    marketingBrandConfigurationEntity,
    marketingChannelConfigurationEntity,
    marketingContentStatusEntity,
    marketingContentTypeEntity,
    marketingMarketingRecommendationEntity,
    marketingNewsletterEntity,
    marketingSocialPostEntity,
} from 'mj_generatedentities';
import { MarketingScheduledJob } from '../../../../types';

interface SocialPlatformRow { ID: string; Name: string; }

type RunState = 'idle' | 'running' | 'queued' | 'error';

@Component({
    standalone: false,
    selector: 'mkt-ws-foundation-activity-log',
    template: `
<mkt-ws-page-head
    eyebrow="Foundation · Activity log"
    title="Marketing scheduled jobs."
    sub="Trigger any marketing scheduled job to run immediately, bypassing its cron schedule.">
    <button class="al__refresh" actions (click)="refresh()" [disabled]="loading">
        <i class="fa-solid" [class.fa-arrows-rotate]="!loading" [class.fa-spinner]="loading" [class.fa-spin]="loading"></i>
        Refresh
    </button>
</mkt-ws-page-head>

<div *ngIf="loading && jobs.length === 0" class="al__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading jobs…</div>

<mkt-ws-coming-soon
    *ngIf="!loading && jobs.length === 0"
    icon="fa-calendar-xmark"
    title="No marketing scheduled jobs"
    body="Once marketing scheduled jobs are configured, they'll appear here.">
</mkt-ws-coming-soon>

<div *ngIf="jobs.length > 0" class="al__grid">
    <div *ngFor="let job of jobs" class="al__card">
        <div class="al__card-head">
            <div class="al__title"><i class="fa-solid fa-robot"></i> {{ job.Name }}</div>
            <mkt-ws-pill [variant]="job.Status === 'Active' ? 'good' : 'warn'">{{ job.Status }}</mkt-ws-pill>
        </div>
        <div class="al__desc" *ngIf="job.Description">{{ job.Description }}</div>
        <div class="al__meta">
            <div class="al__meta-row">
                <span class="al__meta-label">Schedule</span>
                <code class="al__meta-value">{{ job.CronExpression || '—' }}</code>
            </div>
            <div class="al__meta-row">
                <span class="al__meta-label">Last run</span>
                <span class="al__meta-value">{{ formatJobDate(job.LastRunAt) }}</span>
            </div>
            <div class="al__meta-row">
                <span class="al__meta-label">Next run</span>
                <span class="al__meta-value">{{ formatJobDate(job.NextRunAt) }}</span>
            </div>
        </div>
        <div class="al__card-foot">
            <button class="al__run"
                    [disabled]="job.Status !== 'Active' || jobRunState[job.ID] === 'running'"
                    (click)="runJob(job)">
                <i class="fa-solid"
                   [class.fa-play]="jobRunState[job.ID] !== 'running'"
                   [class.fa-spinner]="jobRunState[job.ID] === 'running'"
                   [class.fa-spin]="jobRunState[job.ID] === 'running'"></i>
                {{ jobRunState[job.ID] === 'running' ? 'Triggering…' : 'Run now' }}
            </button>
            <span *ngIf="jobRunState[job.ID] === 'queued'" class="al__msg al__msg--ok">
                <i class="fa-solid fa-check"></i> {{ jobRunMessage[job.ID] }}
            </span>
            <span *ngIf="jobRunState[job.ID] === 'error'" class="al__msg al__msg--err">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ jobRunMessage[job.ID] }}
            </span>
        </div>
    </div>
</div>
    `,
    styles: [`
        :host { display: block; }
        .al__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .al__refresh { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 500; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-secondary, #71717a); cursor: pointer; font-family: inherit; }
        .al__refresh:hover:not(:disabled) { color: var(--mj-text-primary, #1a1a1a); }
        .al__refresh:disabled { opacity: 0.6; cursor: not-allowed; }
        .al__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 16px; }
        .al__card { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px 20px; }
        .al__card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
        .al__title { font-size: 14px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); display: flex; align-items: center; gap: 8px; }
        .al__title i { color: var(--mj-text-secondary, #71717a); font-size: 13px; }
        .al__desc { font-size: 12.5px; color: var(--mj-text-secondary, #71717a); line-height: 1.5; margin-bottom: 12px; }
        .al__meta { display: flex; flex-direction: column; gap: 6px; padding: 10px 0; border-top: 1px solid var(--mj-border-default, #e0e0e0); border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .al__meta-row { display: flex; justify-content: space-between; align-items: center; font-size: 12px; }
        .al__meta-label { color: var(--mj-text-secondary, #71717a); }
        .al__meta-value { color: var(--mj-text-primary, #1a1a1a); font-weight: 500; }
        code.al__meta-value { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; background: var(--mj-bg-page, #f5f5f5); padding: 2px 6px; border-radius: 3px; }
        .al__card-foot { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
        .al__run { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600; border: 1px solid var(--mj-brand-primary, #6366f1); background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); cursor: pointer; font-family: inherit; transition: filter 0.15s; }
        .al__run:hover:not(:disabled) { filter: brightness(1.1); }
        .al__run:disabled { opacity: 0.5; cursor: not-allowed; }
        .al__msg { font-size: 12px; display: inline-flex; align-items: center; gap: 5px; }
        .al__msg--ok { color: var(--mj-color-success-400, #22c55e); }
        .al__msg--err { color: var(--mj-status-error, #ef4444); }
    `]
})
export class FoundationActivityLogSection implements OnChanges {
    @Input() companyId = '';
    @Input() companyName = '';
    // The workspace shell flows the same input map to every section via ngComponentOutlet.
    // These inputs are accepted (not used) so setInput() doesn't throw; the only refresh
    // trigger is companyId — see ngOnChanges below.
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() socialPosts: marketingSocialPostEntity[] = [];
    @Input() newsletters: marketingNewsletterEntity[] = [];
    @Input() recommendations: marketingMarketingRecommendationEntity[] = [];
    @Input() contentStatuses: marketingContentStatusEntity[] = [];
    @Input() contentTypes: marketingContentTypeEntity[] = [];
    @Input() blogStatuses: marketingBlogStatusEntity[] = [];
    @Input() socialPlatforms: SocialPlatformRow[] = [];
    @Input() channelConfigs: marketingChannelConfigurationEntity[] = [];
    @Input() brandConfig: marketingBrandConfigurationEntity | null = null;
    @Input() isLoadingContent = false;
    @Input() navigate: (path: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    jobs: MarketingScheduledJob[] = [];
    jobRunState: Record<string, RunState> = {};
    jobRunMessage: Record<string, string> = {};

    ngOnChanges(changes: SimpleChanges): void {
        // Refresh only when companyId changes — the job list is global, not per-input.
        if (changes['companyId']) {
            void this.refresh();
        }
    }

    async refresh(): Promise<void> {
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const result = await rv.RunView<MarketingScheduledJob>({
                EntityName: 'MJ: Scheduled Jobs',
                ExtraFilter: `Name LIKE '%Marketing%' OR Name LIKE '%CMO%'`,
                OrderBy: 'Name ASC',
                ResultType: 'simple',
            });
            if (result.Success) {
                this.jobs = result.Results || [];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[FoundationActivityLog] refresh failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    async runJob(job: MarketingScheduledJob): Promise<void> {
        if (this.jobRunState[job.ID] === 'running') return;
        if (job.Status !== 'Active') {
            this.jobRunState[job.ID] = 'error';
            this.jobRunMessage[job.ID] = `Job is ${job.Status}; only Active jobs can be triggered.`;
            this.cdr.detectChanges();
            return;
        }
        this.jobRunState[job.ID] = 'running';
        this.jobRunMessage[job.ID] = '';
        this.cdr.detectChanges();
        const result = await this.runAction('Run Scheduled Job Now', [{ Name: 'JobID', Value: job.ID }]);
        if (result.success) {
            this.jobRunState[job.ID] = 'queued';
            this.jobRunMessage[job.ID] = result.message || 'Dispatched for immediate execution.';
        } else {
            this.jobRunState[job.ID] = 'error';
            this.jobRunMessage[job.ID] = result.message || 'Failed to trigger job.';
        }
        this.cdr.detectChanges();
    }

    formatJobDate(d: Date | string | null | undefined): string {
        if (!d) return '—';
        const date = new Date(d);
        if (isNaN(date.getTime())) return '—';
        return date.toLocaleString();
    }

    private async runAction(actionName: string, params: { Name: string; Value: string }[]): Promise<{ success: boolean; message: string }> {
        try {
            const gql = Metadata.Provider as GraphQLDataProvider;
            const rv = new RunView();
            const result = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='${actionName}'`,
                MaxRows: 1,
                ResultType: 'simple',
                Fields: ['ID'],
            });
            if (!result.Success || result.Results.length === 0) {
                return { success: false, message: `Action '${actionName}' not found` };
            }
            const response = await gql.ExecuteGQL(
                `mutation RunAction($input: RunActionInput!) { RunAction(input: $input) { Success Message ResultCode } }`,
                {
                    input: {
                        ActionID: result.Results[0].ID,
                        Params: params.map(p => ({ Name: p.Name, Value: p.Value, Type: 'Input' })),
                    },
                }
            );
            const r = response?.RunAction;
            return { success: !!r?.Success, message: r?.Message || 'Unknown' };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            return { success: false, message: msg };
        }
    }
}
